/**
 * @file Week-level Calendar entry row allocation helpers.
 */

import { entryOccursOnDate } from "./calendarDateUtils.js";

function getEntryKey(entry = {}) {
    return [
        entry.sourceType || entry.renderKind || "entry",
        entry.sourceId || entry.id || entry.title || "",
        entry.startDate || "",
        entry.endDate || entry.startDate || "",
    ].join(":");
}

function getSpanLength(entry = {}) {
    const start = new Date(`${entry.startDate || ""}T00:00:00`);
    const end = new Date(`${entry.endDate || entry.startDate || ""}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return 1;
    }
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

function getEntryTypeRank(entry = {}) {
    const type = entry.sourceType || entry.renderKind || "";
    if (type === "leave") {return 0;}
    if (type === "event") {return 1;}
    if (type === "reminder") {return 2;}
    return 3;
}

function compareEntries(a, b) {
    const aMultiDay = getSpanLength(a) > 1;
    const bMultiDay = getSpanLength(b) > 1;
    if (aMultiDay !== bMultiDay) {
        return aMultiDay ? -1 : 1;
    }
    if ((a.startDate || "") !== (b.startDate || "")) {
        return String(a.startDate || "").localeCompare(String(b.startDate || ""));
    }
    if ((a.endDate || a.startDate || "") !== (b.endDate || b.startDate || "")) {
        return String(b.endDate || b.startDate || "")
            .localeCompare(String(a.endDate || a.startDate || ""));
    }
    const typeDiff = getEntryTypeRank(a) - getEntryTypeRank(b);
    if (typeDiff !== 0) {return typeDiff;}
    return String(a.title || "").localeCompare(String(b.title || ""));
}

function getWeekEntrySpan(entry, weekDays) {
    const indexes = weekDays
        .map((day, index) => entryOccursOnDate(entry, day.dateKey) ? index : -1)
        .filter((index) => index >= 0);
    if (indexes.length === 0) {return null;}
    return {
        startIndex: Math.min(...indexes),
        endIndex: Math.max(...indexes),
    };
}

function findAvailableRow(rows, span) {
    let rowIndex = 0;
    while (true) {
        const occupied = rows[rowIndex] || Array(7).fill(false);
        let isAvailable = true;
        for (let index = span.startIndex; index <= span.endIndex; index += 1) {
            if (occupied[index]) {
                isAvailable = false;
                break;
            }
        }
        if (isAvailable) {
            rows[rowIndex] = occupied;
            return rowIndex;
        }
        rowIndex += 1;
    }
}

function markRowOccupied(row, span) {
    for (let index = span.startIndex; index <= span.endIndex; index += 1) {
        row[index] = true;
    }
}

function createEmptySlots(maxVisibleRows) {
    return Array.from({ length: maxVisibleRows }, () => null);
}

/**
 * Builds stable per-day Calendar entry slots for one month grid.
 * @param {Array<object>} days Calendar month grid days.
 * @param {Array<object>} entries Display-safe Calendar entries.
 * @param {number} maxVisibleRows Visible entry rows before overflow.
 * @returns {{slotsByDate: Map<string, Array<object|null>>, hiddenCountByDate: Map<string, number>}} Layout.
 */
export function buildCalendarEntryLayout(days, entries, maxVisibleRows) {
    const slotsByDate = new Map(days.map((day) => [
        day.dateKey,
        createEmptySlots(maxVisibleRows),
    ]));
    const hiddenCountByDate = new Map(days.map((day) => [day.dateKey, 0]));

    for (let weekStart = 0; weekStart < days.length; weekStart += 7) {
        const weekDays = days.slice(weekStart, weekStart + 7);
        const uniqueEntries = new Map();
        entries.forEach((entry) => {
            if (weekDays.some((day) => entryOccursOnDate(entry, day.dateKey))) {
                uniqueEntries.set(getEntryKey(entry), entry);
            }
        });

        const rows = [];
        [...uniqueEntries.values()]
            .sort(compareEntries)
            .forEach((entry) => {
                const span = getWeekEntrySpan(entry, weekDays);
                if (!span) {return;}
                const rowIndex = findAvailableRow(rows, span);
                markRowOccupied(rows[rowIndex], span);
                weekDays.forEach((day) => {
                    if (!entryOccursOnDate(entry, day.dateKey)) {return;}
                    if (rowIndex < maxVisibleRows) {
                        slotsByDate.get(day.dateKey)[rowIndex] = {
                            entry,
                            rowIndex,
                        };
                    } else {
                        hiddenCountByDate.set(
                            day.dateKey,
                            (hiddenCountByDate.get(day.dateKey) || 0) + 1,
                        );
                    }
                });
            });
    }

    return {slotsByDate, hiddenCountByDate};
}
